// Filter chip bar for the SOW section. Mirrors `MeetingFilterChipBar` —
// search input + multi-select Status chip + single-select Date range
// chip + binary Include-archived chip. The popover affordance, debounce
// strategy, and keyboard handling are the same so the two surfaces
// read with one chip vocabulary.

import { useEffect, useRef, useState } from "react";
import type { SowStatus } from "../../../../types";
import type {
  SowDateRangeFilter,
  SowFilters,
} from "./applySowFilters";
import DebouncedSearchInput from "../shared/DebouncedSearchInput";

export interface SowFilterChipBarProps {
  filters: SowFilters;
  onFiltersChange: (next: SowFilters) => void;
  search: string;
  onSearchChange: (next: string) => void;
  resetCounter?: number;
}

interface OptionDef<V extends string> {
  value: V;
  label: string;
}

const STATUS_OPTIONS: OptionDef<SowStatus>[] = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const DATE_RANGE_OPTIONS: OptionDef<SowDateRangeFilter>[] = [
  { value: "next-30-days", label: "Next 30 days" },
  { value: "past-30-days", label: "Past 30 days" },
  { value: "this-quarter", label: "This quarter" },
  { value: "all-time", label: "All time" },
];

const CHIP_BASE =
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-caption font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400";
const CHIP_INACTIVE =
  "border-slate-300 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700";
const CHIP_ACTIVE =
  "border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-500 dark:bg-brand-950 dark:text-brand-300";
const POPOVER_CLASS =
  "absolute z-20 mt-1 min-w-[180px] max-h-[60vh] overflow-y-auto rounded-md border border-slate-200 bg-white p-2 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800";

interface MultiChipProps<V extends string> {
  chipKey: string;
  label: string;
  options: OptionDef<V>[];
  selected: V[];
  onToggle: (next: V[]) => void;
}

function MultiChip<V extends string>({
  chipKey,
  label,
  options,
  selected,
  onToggle,
}: MultiChipProps<V>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isActive = selected.length > 0;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const toggle = (v: V) => {
    onToggle(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`${CHIP_BASE} ${isActive ? CHIP_ACTIVE : CHIP_INACTIVE}`}
        data-testid={`sow-filter-chip-${chipKey}`}
      >
        <span>{label}</span>
        {isActive && (
          <span className="rounded bg-white px-1 text-[10px] text-brand-600 dark:bg-brand-900 dark:text-brand-300">
            {selected.length}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid={`sow-filter-popover-${chipKey}`}
          role="listbox"
          aria-multiselectable="true"
          className={POPOVER_CLASS}
        >
          <ul className="space-y-1">
            {options.map((opt) => {
              const id = `sow-filter-${chipKey}-${opt.value}`;
              return (
                <li key={opt.value}>
                  <label
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-body-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <input
                      id={id}
                      type="checkbox"
                      checked={selected.includes(opt.value)}
                      onChange={() => toggle(opt.value)}
                      className="accent-brand-600"
                    />
                    <span>{opt.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

interface SingleChipProps<V extends string> {
  chipKey: string;
  label: string;
  options: OptionDef<V>[];
  value: V;
  defaultValue: V;
  onChange: (next: V) => void;
}

function SingleChip<V extends string>({
  chipKey,
  label,
  options,
  value,
  defaultValue,
  onChange,
}: SingleChipProps<V>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isActive = value !== defaultValue;
  const selectedLabel = options.find((o) => o.value === value)?.label ?? label;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`${CHIP_BASE} ${isActive ? CHIP_ACTIVE : CHIP_INACTIVE}`}
        data-testid={`sow-filter-chip-${chipKey}`}
      >
        <span>{label}</span>
        {isActive && <span className="text-brand-700">· {selectedLabel}</span>}
      </button>
      {open && (
        <div
          data-testid={`sow-filter-popover-${chipKey}`}
          role="listbox"
          className={POPOVER_CLASS}
        >
          <ul className="space-y-1">
            {options.map((opt) => {
              const id = `sow-filter-${chipKey}-${opt.value}`;
              return (
                <li key={opt.value}>
                  <label
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-body-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <input
                      id={id}
                      type="radio"
                      name={chipKey}
                      checked={value === opt.value}
                      onChange={() => {
                        onChange(opt.value);
                        setOpen(false);
                      }}
                      className="accent-brand-600"
                    />
                    <span>{opt.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function SowFilterChipBar({
  filters,
  onFiltersChange,
  search,
  onSearchChange,
  resetCounter,
}: SowFilterChipBarProps) {
  const update = <K extends keyof SowFilters>(key: K, value: SowFilters[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="sow-filter-chip-bar">
      <DebouncedSearchInput
        value={search}
        onDebouncedChange={onSearchChange}
        resetCounter={resetCounter}
        ariaLabel="Search SOWs"
        placeholder="Search SOWs…"
        data-testid="sow-filter-search"
      />
      <MultiChip
        chipKey="status"
        label="Status"
        options={STATUS_OPTIONS}
        selected={filters.status}
        onToggle={(next) => update("status", next)}
      />
      <SingleChip
        chipKey="date-range"
        label="Date range"
        options={DATE_RANGE_OPTIONS}
        value={filters.dateRange}
        defaultValue="all-time"
        onChange={(next) => update("dateRange", next)}
      />
      <button
        type="button"
        onClick={() => update("includeArchived", !filters.includeArchived)}
        aria-pressed={filters.includeArchived}
        className={`${CHIP_BASE} ${filters.includeArchived ? CHIP_ACTIVE : CHIP_INACTIVE}`}
        data-testid="sow-filter-chip-archived"
      >
        Include archived
      </button>
    </div>
  );
}
